import {
  BioQueryKey,
  AnalyticsQueryKey,
  BrandHubQueryKey,
  ContentPlanQueryKey,
  DashboardQueryKey,
  PostsQueryKey,
  QueryKey,
  ReportsQueryKey,
  NotificationsQueryKey,
  SettingsQueryKey,
  SocialLoginQueryKey,
} from 'app/utils/queryKey';
import { UsersListParams } from 'app/modules/settings/modules/users/types/user';
import { AuthorizationPagesFilter } from 'app/modules/settings/types/authorizedAccount';
import {
  PostsRequestParams,
  TasksRequestParams,
} from 'app/modules/dashboard/api';
import {
  GetPostsMetaParams,
  GetPostsParams,
  GetPostTextInfoParams,
} from 'app/modules/posts/api/posts';
import { WidgetsSetupTarget } from 'app/modules/posts/api/widgetsSetup';
import { AudienceRestrictionField } from 'app/modules/posts/modules/detail/modules/audienceRestrictions/constants/audienceRestrictions';
import {
  AudienceRestrictions,
  AudienceRestrictionsOptions,
} from 'app/modules/posts/types/AudienceRestrictionsOptions';
import { TrashPostsParams } from 'api/post';
import type { AnalyticsReportsListParams } from 'api/analytics-reports/types';
import type { InAppNotificationsQueryParams } from 'app/api/inAppNotifications';

export const queryKey = {
  postDetail: (id: number) => PostsQueryKey('postDetail', [id]),
  postRevisions: (params?: { postId: number }) =>
    PostsQueryKey('postVersions', params),
  postRevisionDetail: (params: { postId: number; revisionId: number }) =>
    PostsQueryKey('postRevisionDetail', params),
  aiContentOptions: (params?: { planType: string }) =>
    PostsQueryKey('aiContentOptions', params),
  posts: (params?: GetPostsParams) => PostsQueryKey('posts', params),
  onboardingChecklistPosts: (params?: GetPostsParams) =>
    PostsQueryKey('onboardingChecklistPosts', params),
  bulkApprovalPosts: (params?: GetPostsParams) =>
    PostsQueryKey('bulkApprovalPosts', params),
  postsMeta: (params?: GetPostsMetaParams) =>
    PostsQueryKey('postsMeta', params),
  postComposerDetail: 'postComposerDetail',
  postToDelete: (id?: number | null) => PostsQueryKey('postToDelete', [id]),
  postScheduleValidation: (id: number) =>
    PostsQueryKey('postScheduleValidation', [id]),
  approvalData: (ids: number[], type: string) =>
    PostsQueryKey('approvalData', [...ids, type]),
  pageDetail: (id?: number) => PostsQueryKey('pageDetail', [id]),
  localPages: (id?: number) => PostsQueryKey('localPages', [id]),
  postTranslation: (postId: number) =>
    PostsQueryKey('postTranslation', [postId]),
  pageTimezone: (id?: number) => PostsQueryKey('pageTimezone', [id]),
  pageTags: (pageId?: number | null) => PostsQueryKey('pageTags', pageId),
  recentTags: (pageId?: number | null) => PostsQueryKey('recentTags', pageId),
  postTextInfo: (params: GetPostTextInfoParams) =>
    PostsQueryKey('postTextInfo', params),
  widgetsSetup: (params: { target: WidgetsSetupTarget }) =>
    PostsQueryKey('widgetsSetup', params),
  multipleScheduleValidations: 'multipleScheduleValidations',
  allMediaAlbums: 'allMediaAlbums',
  albumMedia: (albumId?: number) => PostsQueryKey('albumMedia', albumId),
  videoMedium: 'videoMedium',
  linkDomainVerification: (input: { pageId: number; url: string }) =>
    PostsQueryKey('linkDomainVerification', input),
  /**
   * @deprecated deprecated use postCommentsThread instead
   */
  commentsForPost: (id: number) => PostsQueryKey('commentsForPost', [id]),
  postCommentsThread: (params?: { postId: number; thread: string }) =>
    PostsQueryKey('postCommentsThread', params),
  postCommentsPinnedThread: (params?: { postId: number; thread: string }) =>
    PostsQueryKey('postCommentsPinnedThread', params),
  postCommentsSummary: (params?: { postId: number; thread: string }) =>
    PostsQueryKey('postCommentsSummary', params),
  publicPostComments: (params?: { postId: number; token: string }) =>
    PostsQueryKey('postComments', params),
  pageUsersWithMobileApp: (pageId: number) =>
    PostsQueryKey('usersWithMobileApp', [pageId]),
  // Keyed by page as well as query: the search is page-scoped now, so two
  // pages asking the same thing are two different questions and must not share
  // a cache entry.
  locationsSearch: (input: string, pageId?: number) =>
    PostsQueryKey('instagramLocations', [input, pageId]),
  instagramAudioTracks: (params: {
    pageId: number;
    audioType: string;
    searchQuery: string;
  }) => PostsQueryKey('instagramAudioTracks', params),
  instagramAudioTrack: (params: { pageId: number; audioId: string }) =>
    PostsQueryKey('instagramAudioTrack', params),
  instagramAudioPublicTrack: (params: { postId: number; token: string }) =>
    PostsQueryKey('instagramAudioPublicTrack', params),
  commentsForPostMentionableUsers: (id: number) =>
    PostsQueryKey('commentsForPostMentionableUsers', [id]),
  pageImageTemplates: (id?: number) =>
    PostsQueryKey('pageImageTemplates', [id]),
  hashtagPresets: 'hashtagPresets',
  shortenLinks: 'shortenLinks',
  shortenLinkDetail: 'shortenLinkDetail',
  hashtagPresetsAssignablePages: 'hashtagPresetsAssignablePages',
  postsOnFBPage: 'postsOnFBPage',
  profileGroupDetail: 'profileGroupDetail',
  userOptionsForProfileGroup: 'userOptionsForProfileGroup',
  userOptionsForSelect: 'userOptionsForSelect',
  mentionableUsers: 'mentionableUsers',
  allInspirations: () => PostsQueryKey('inspirations'),
  inspirations: (params?: {
    startDate: string;
    endDate: string;
    pages: number[];
  }) => PostsQueryKey('inspirations', [params]),
  inspirationDetail: 'inspirationDetail',
  pageAudienceRestrictions: (params: {
    term: string;
    type: AudienceRestrictionField;
    pageType: number;
  }) => PostsQueryKey('pageAudienceRestrictions', [params]),
  pageDetailWithAudienceRestrictions: (pageId: number) =>
    PostsQueryKey('pageDetailWithAudienceRestrictions', [pageId]),
  audienceRestrictionsOptionsByOptionIds:
    'audienceRestrictionsOptionsByOptionIds',
  audienceSize: (params: {
    pageId: number;
    formState: AudienceRestrictionsOptions;
  }) => PostsQueryKey('audienceSize', [params]),
  audiencePresets: (pageType: number) =>
    PostsQueryKey('audiencePresets', [pageType]),
  audiencePresetOptions: (params: {
    preset: AudienceRestrictions | null;
    pageType: number;
  }) => PostsQueryKey('audiencePresetOptions', [params]),
  publicPostPreview: ({ token, id }: { token: string; id: number }) =>
    PostsQueryKey('publicPostPreview', [id, token]),
  postPublicPreviewUrl: 'postPublicPreviewUrl',
  assignedPageUsers: (ids?: number[]) =>
    PostsQueryKey('assignedPageUsers', ids),
  timezones: () => PostsQueryKey('timezones'),
  referralLink: (email: string) => PostsQueryKey('referralLink', [email]),
  textEditorSuggestions: (params: (string | number)[]) =>
    QueryKey('textEditor', 'suggestions', params),
  postActivityLog: (postId: number) =>
    PostsQueryKey('postActivityLog', [postId]),
  dashboardProfiles: (params?: { sortBy: string }) =>
    DashboardQueryKey('dashboardProfiles', params),
  dashboardBrands: () => DashboardQueryKey('dashboardBrands'),
  dashboardProfileGroups: (params?: { sortBy: string }) =>
    DashboardQueryKey('dashboardProfileGroups', params),
  dashboardPosts: (params?: PostsRequestParams) =>
    DashboardQueryKey('dashboardPosts', params),
  dashboardFailedPosts: (params?: { limit: number; page: number }) =>
    DashboardQueryKey('dashboardFailedPosts', params),
  dashboardMyTasks: (params?: TasksRequestParams) =>
    DashboardQueryKey('dashboardMyTasks', params),
  dashboardTasksStatus: (params?: TasksRequestParams) =>
    DashboardQueryKey('dashboardTasksStatus', params),
  dashboardSocialBites: () => DashboardQueryKey('dashboardSocialBites'),
  dashboardPendingApprovals: (params?: GetPostsParams) =>
    DashboardQueryKey('dashboardPendingApprovals', params),
  pagesContentPlans: (params?: number[]) =>
    ContentPlanQueryKey('pagesContentPlans', params),
  brandHubProfiles: () => BrandHubQueryKey('profiles'),
  brandHubProfile: (id: string | number) => BrandHubQueryKey('profile', [id]),
  brandHubProfileAnalytics: (id: string | number) =>
    BrandHubQueryKey('profileAnalytics', [id]),
  brandHubProfileInsights: (id: string | number) =>
    BrandHubQueryKey('profileInsights', [id]),
  brandHubProfileCreatives: (params?: { id: string | null }) =>
    BrandHubQueryKey('profileCreatives', params),
  brandHubCreateCreative: (id: string | number) =>
    BrandHubQueryKey('createCreative', id),
  brandHubCreativesOptions: () => BrandHubQueryKey('creativesOptions'),
  transformTextOptions: () => PostsQueryKey('transformTextOptions'),
  socialLoginPages: (userKey?: string) =>
    SocialLoginQueryKey('pages', [userKey]),
  socialLoginConnectedPagesExternalIds: (userKey?: string) =>
    SocialLoginQueryKey('connectedPagesExternalIds', [userKey]),
  onboardingConnectedPagesExternalIds: (pageType: number) =>
    SocialLoginQueryKey('onboardingConnectedPagesExternalIds', [pageType]),
  authorizedAccounts: () => SettingsQueryKey('authorizedAccounts'),
  authorizationPages: (filter?: AuthorizationPagesFilter) =>
    SettingsQueryKey('authorizationPages', [filter]),
  billing: (statusOverride?: string) =>
    SettingsQueryKey('billing', [statusOverride ?? 'default']),
  billingSubscription: () => SettingsQueryKey(['billing', 'subscription']),
  billingUsage: () => SettingsQueryKey(['billing', 'usage']),
  billingNextInvoice: () => SettingsQueryKey(['billing', 'nextInvoice']),
  billingAddress: () => SettingsQueryKey(['billing', 'address']),
  // Outside the billing prefix: no billing mutation can change the answer,
  // and each refetch is another VIES lookup.
  vatNumberSuggestion: (country: string, businessId: string) =>
    SettingsQueryKey(['vatNumberSuggestion', country, businessId]),
  billingPaymentMethod: () => SettingsQueryKey(['billing', 'paymentMethod']),
  billingPaymentMethodClientToken: () =>
    SettingsQueryKey(['billing', 'paymentMethod', 'clientToken']),
  billingPaymentMethods: () => SettingsQueryKey(['billing', 'paymentMethods']),
  billingInvoices: (params?: { limit?: number; cursor?: string }) =>
    SettingsQueryKey(['billing', 'invoices'], params),
  billingInvoicesInfinite: (params?: { limit?: number }) =>
    SettingsQueryKey(['billing', 'invoicesInfinite'], params),
  billingInvoicePaymentInstructions: (invoiceId: string) =>
    SettingsQueryKey(['billing', 'invoicePaymentInstructions', invoiceId]),
  billingAddons: () => SettingsQueryKey(['billing', 'addons']),
  billingModules: () => SettingsQueryKey(['billing', 'modules']),
  billingPlans: () => SettingsQueryKey(['billing', 'plans']),
  billingTrial: () => SettingsQueryKey(['billing', 'trial']),
  billingTrialUnlockedFeatures: () =>
    SettingsQueryKey(['billing', 'trialUnlockedFeatures']),
  billingTrialSocialProof: () =>
    SettingsQueryKey(['billing', 'trialSocialProof']),
  billingSetupPrerequisites: () =>
    SettingsQueryKey(['billing', 'setupPrerequisites']),
  billingSetupSummary: (params: {
    planId: string;
    cycle: 'monthly' | 'annual';
    country?: string;
    vatNo?: string;
    couponCode?: string;
  }) => SettingsQueryKey(['billing', 'setupSummary'], params),
  billingV1UpgradePreview: (params: {
    planId: string;
    cycle: 'monthly' | 'annual';
  }) => SettingsQueryKey(['billing', 'v1UpgradePreview'], params),
  billingChangePreview: (params: {
    planId: string;
    cycle: 'monthly' | 'annual';
    couponCode?: string;
  }) => SettingsQueryKey(['billing', 'changePreview'], params),
  billingEntitlements: () => SettingsQueryKey(['billing', 'entitlements']),
  postPatches: (postId?: number) => PostsQueryKey('postPatches', [postId]),
  reports: (params?: {}) => ReportsQueryKey('reports', params),
  reportDetail: (id: number) => ReportsQueryKey('reportDetail', [id]),
  reportsUserUseMeridianTime: (token?: string) =>
    ReportsQueryKey('reportsUserUseMeridianTime', [token]),
  reportsReportAutomation: (id: number) =>
    ReportsQueryKey('reportsReportAutomation', [id]),
  postsContentPlans: (params?: number[]) =>
    PostsQueryKey('postsContentPlans', params),
  brandHubAnalyticsProfiles: () => BrandHubQueryKey('analyticsProfiles'),
  brandHubContentIdeas: (params: {
    brandProfileId: string;
    planType: string;
  }) => BrandHubQueryKey('contentIdeas', params),
  brandHubSyncBatchStatus: (batchId: string | null) =>
    BrandHubQueryKey('syncBatchStatus', [batchId]),
  approvalsPosts: (params?: GetPostsParams) =>
    PostsQueryKey('approvalsPosts', params),
  brandHubExecutionStatusProgress: (id?: string) =>
    BrandHubQueryKey('executionStatusProgress', [id]),
  usersList: (params?: UsersListParams) =>
    SettingsQueryKey('usersList', params),
  userDetail: (userId: number) => SettingsQueryKey('userDetail', [userId]),
  usersFilterOptions: () => SettingsQueryKey('usersFilterOptions'),
  usersAvailableProfiles: () => SettingsQueryKey('usersAvailableProfiles'),
  profileGroups: () => SettingsQueryKey('profileGroups'),
  mfaStatus: () => SettingsQueryKey('mfaStatus'),
  connectedAccounts: () => SettingsQueryKey('connectedAccounts'),
  notificationSettings: (channel: string) =>
    SettingsQueryKey('notificationSettings', { channel }),
  notificationDevices: () => SettingsQueryKey('notificationDevices'),
  inAppNotifications: (params?: InAppNotificationsQueryParams) =>
    NotificationsQueryKey('inAppNotifications', params),
  inAppNotificationsSummary: () =>
    NotificationsQueryKey('inAppNotificationsSummary'),
  inAppNotificationsCount: () =>
    NotificationsQueryKey('inAppNotificationsCount'),
  aiModels: (type: 'chat' | 'image') => BrandHubQueryKey('aiModels', [type]),
  brandHubTokenUsage: () => BrandHubQueryKey('tokenUsage'),
  trashedPosts: (params?: TrashPostsParams) =>
    PostsQueryKey('trashedPosts', params),
  trashedPostsCount: (params?: { profileIds?: number[] }) =>
    PostsQueryKey('trashedPostsCount', params),
  translationLanguages: () => PostsQueryKey('translationLanguages'),
  demoContentState: () => SettingsQueryKey('demoContentState'),
  bioPages: () => BioQueryKey('bioPages'),
  bioPage: (id: string) => BioQueryKey('bioPage', [id]),
  bioTemplates: () => BioQueryKey('bioTemplates'),
  bioAnalytics: (id: string) => BioQueryKey('bioAnalytics', [id]),
  analyticsHealth: () => AnalyticsQueryKey('health'),
  analyticsOverview: ({
    pageIds,
    since,
    until,
    compareSince,
    compareUntil,
  }: {
    pageIds: number[];
    since: string;
    until: string;
    compareSince?: string;
    compareUntil?: string;
  }) =>
    AnalyticsQueryKey('overview', [
      pageIds,
      since,
      until,
      compareSince,
      compareUntil,
    ]),
  analyticsAudience: (pageIds: number[], since: string, until: string) =>
    AnalyticsQueryKey('audience', [pageIds, since, until]),
  analyticsBestTimeToPost: (pageIds: number[], since: string, until: string) =>
    AnalyticsQueryKey('bestTimeToPost', [pageIds, since, until]),
  analyticsPerformance: (
    pageIds: number[],
    since: string,
    until: string,
    compareSince?: string,
    compareUntil?: string,
  ) =>
    AnalyticsQueryKey('performance', [
      pageIds,
      since,
      until,
      'time_series',
      compareSince,
      compareUntil,
    ]),
  analyticsContent: (
    pageIds: number[],
    since: string,
    until: string,
    extra?: unknown,
  ) => AnalyticsQueryKey('content', [pageIds, since, until, extra]),
  analyticsSyncBatch: (batchId: string | string[]) =>
    AnalyticsQueryKey('syncBatch', [
      typeof batchId === 'string'
        ? batchId
        : [...batchId].sort((a, b) => a.localeCompare(b)).join(','),
    ]),
  analyticsReportsTemplates: () => AnalyticsQueryKey('reportsTemplates'),
  analyticsReports: (params?: AnalyticsReportsListParams) =>
    AnalyticsQueryKey('reports', params),
  analyticsReportRun: (reportId: number, runId: number) =>
    AnalyticsQueryKey('reportRun', [reportId, runId]),
  analyticsReportPreview: (reportId: number) =>
    AnalyticsQueryKey('reportPreview', [reportId]),
  /** Keyed by the credentials too: a different token or account is different data. */
  analyticsExportPreview: (
    reportId: number,
    token: string | null | undefined,
    accountId: number | undefined,
  ) => AnalyticsQueryKey('exportPreview', [reportId, token, accountId]),
  analyticsReport: (reportId: number) =>
    AnalyticsQueryKey('report', [reportId]),
  analyticsReportShareLink: (reportId: number) =>
    AnalyticsQueryKey('reportShareLink', [reportId]),
};
